"use client";

import { cn } from "@ui/lib";
import { CheckCircle2Icon, CircleIcon, Loader2Icon } from "lucide-react";
import { GENERATING_STEPS } from "../lib/copy";

export type GeneratingStep = 0 | 1 | 2;

/**
 * Progress while a generation runs. Prompts and personas are written side by
 * side once the snapshot exists (one job, framework calls and the persona
 * call in parallel), so from step 2 the last two rows spin together. Without
 * personas the fourth row is dropped.
 */
export function GeneratingPanel({
	domain,
	step,
	includePersonas,
	frameworkCount,
}: {
	domain: string | null;
	/** 0 reading the site, 1 building the snapshot, 2 prompts (+ personas). */
	step: GeneratingStep;
	includePersonas: boolean;
	frameworkCount: number;
}) {
	const steps = includePersonas
		? GENERATING_STEPS
		: GENERATING_STEPS.slice(0, 3);

	const statusFor = (i: number): "done" | "active" | "pending" => {
		if (i < 2) {
			return i < step ? "done" : i === step ? "active" : "pending";
		}
		return step >= 2 ? "active" : "pending";
	};

	const frameworks = `${frameworkCount} framework${frameworkCount === 1 ? "" : "s"}`;

	return (
		<div className="flex flex-col items-center py-10">
			<Loader2Icon
				className="size-10 animate-spin text-primary motion-reduce:animate-none"
				aria-hidden
			/>
			<p className="mt-4 text-center text-lg font-medium">
				{includePersonas
					? "Building your prompts and personas"
					: "Generating your prompts"}
			</p>
			<p className="mt-1 text-center text-sm text-muted-foreground">
				{domain
					? `For ${domain} with ${frameworks}. This usually takes 30 to 90 seconds.`
					: `With ${frameworks}. This usually takes 30 to 90 seconds.`}
			</p>

			<ol className="mt-6 w-full max-w-sm space-y-2" aria-live="polite">
				{steps.map((label, i) => {
					const status = statusFor(i);
					return (
						<li
							key={label}
							className={cn(
								"flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm",
								status === "active" &&
									"border-primary/40 bg-primary/5",
								status === "pending" && "text-muted-foreground",
							)}
						>
							{status === "done" ? (
								<CheckCircle2Icon
									className="size-4 shrink-0 text-primary"
									aria-hidden
								/>
							) : status === "active" ? (
								<Loader2Icon
									className="size-4 shrink-0 animate-spin text-primary motion-reduce:animate-none"
									aria-hidden
								/>
							) : (
								<CircleIcon
									className="size-4 shrink-0 text-muted-foreground"
									aria-hidden
								/>
							)}
							<span className="min-w-0 flex-1">{label}</span>
							<span className="sr-only">
								{status === "done"
									? "Done"
									: status === "active"
										? "In progress"
										: "Up next"}
							</span>
						</li>
					);
				})}
			</ol>

			<p className="mt-6 max-w-sm text-center text-xs text-muted-foreground">
				Free. No account required. Your results get their own link, so
				you can leave this page and come back to them.
			</p>
		</div>
	);
}
